import React, { useMemo, useState } from 'react';
import { Grid, Box, Typography, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import {
    USAMapBubbleChart,
    HighchartBarChart,
    CustomButtonGroup,
    USACountryChart,
} from '../../../shared';
import {
    DISTRIBUTION_CHART_OPTIONS,
    USA_CHART_OPTIONS,
    hideDistrictMap,
    mapOptions,
} from './constants';
import { BREAKDOWN } from '../../../../constants';
import _ from 'lodash';

const MemoizedUSAMapBubbleChart = React.memo(USAMapBubbleChart);
const hideOverAll = [];
const BreakDowns = {
    66: ['Region - RMD', 'Territory-IROC', 'State'],
    140: ['Region - RMD', 'Territory-IROC', 'State'],
    161: ['Region - RMD', 'Territory-IROC', 'State'],
    default: BREAKDOWN,
};
function DistrictBreakdown() {
    const { company } = useSelector((state) => state.company);
    const finalBreakDowns = useMemo(
        () => BreakDowns[company.id] || BreakDowns.default,
        [company.id],
    );
    const [selectedBreakdown, setSelectedBreakdown] = useState(
        finalBreakDowns[0],
    );
    const {
        overallPhysicians,
        stateOverallPhysicians,
        isLoading,
        distributionRegion,
        districtBreakdownUSAMapData,
        stateDistributionRegion,
        stateBreakdownUSAMapData,
        stateMapSeries,
        territoriesBreakdownUSAMapData,
        distributionTerritories,
        overallTerritoriesPhysicians,
        stateDistributionRegionPercentage,
        distributionRegionPercentage,
    } = useSelector((state) => state.digitalEngagement);

    useMemo(() => {
        if (company.id && hideDistrictMap.includes(company.id)) {
            setSelectedBreakdown(finalBreakDowns[0]);
        } else {
            setSelectedBreakdown(finalBreakDowns[0]);
        }
    }, [company.id]);

    const handleDistrictBreakdown = (option) => {
        setSelectedBreakdown(option);
    };

    const customisation = useMemo(() => {
        const companyId = company.id;
        let chartSeries = [],
            breakdown = '',
            count = 0,
            bubbleMapData = [];

        if (
            companyId &&
            hideDistrictMap.includes(companyId) &&
            ![98].includes(companyId)
        ) {
            chartSeries = stateDistributionRegion;
            breakdown = selectedBreakdown;
            count = stateOverallPhysicians;
            bubbleMapData = stateBreakdownUSAMapData;
        } else if (selectedBreakdown === 'State') {
            chartSeries = [140, 67, 98].includes(companyId)
                ? stateDistributionRegionPercentage
                : stateDistributionRegion;
            breakdown = selectedBreakdown;
            count = stateOverallPhysicians;
        } else if (selectedBreakdown === 'Territory-IROC') {
            chartSeries = distributionTerritories;
            breakdown = selectedBreakdown;
            count = overallTerritoriesPhysicians;
            bubbleMapData = territoriesBreakdownUSAMapData;
        } else {
            chartSeries = [].includes(companyId)
                ? distributionRegionPercentage
                : distributionRegion;
            breakdown = selectedBreakdown;
            count = overallPhysicians;
            bubbleMapData = districtBreakdownUSAMapData;
        }

        return {
            chartSeries,
            breakdown,
            count,
            bubbleMapData,
        };
    }, [
        company.id,
        selectedBreakdown,
        distributionRegion,
        stateDistributionRegion,
    ]);

    const chartOptions = _.cloneDeep(DISTRIBUTION_CHART_OPTIONS);
    chartOptions.chart.height =
        customisation?.chartSeries?.length > 0
            ? customisation.chartSeries.length * 35
            : 0;

    chartOptions.plotOptions.bar.dataLabels.formatter = function () {
        return [140, 67, 98].includes(company.id) &&
            selectedBreakdown === 'State'
            ? this.y + ' %'
            : this.y;
    };

    const titles = {
        97: 'Breakdown of Digital Engagement',
        default: 'Breakdown of Digital Physician Engagement',
    };

    const title = titles[company.id] || titles['default'];
    const sub_titles = {
        97: 'Overall',
        default: 'Overall Physicians',
    };
    const sub_title = sub_titles[company.id] || sub_titles['default'];

    return (
        <Box className="box-sizing-border-box padding-24px">
            <Box className="padding-tb-20 border-bottom-gray flex-row-space-between-center">
                <Box>
                    <Typography className="text-lg-semi-bold gray-900">
                        {customisation.breakdown} {title}
                    </Typography>
                </Box>
                {!hideDistrictMap.includes(company.id) ? (
                    <Box>
                        <CustomButtonGroup
                            data={finalBreakDowns}
                            handleClick={(option) =>
                                handleDistrictBreakdown(option)
                            }
                            key="breakdown"
                            selected={selectedBreakdown}
                        />
                    </Box>
                ) : null}
            </Box>

            <Grid container className="flex-row-center">
                <Grid item className="us-bubble-map-container" lg={7} xl={8}>
                    {/* Location Chart */}
                    {isLoading ? (
                        <Skeleton
                            height={300}
                            variant="rectangular"
                            width="95%"
                        />
                    ) : ['Territory-IROC', 'Region - RMD', 'District'].includes(
                          selectedBreakdown,
                      ) ? (
                        <MemoizedUSAMapBubbleChart
                            chartOptions={mapOptions}
                            seriesData={customisation.bubbleMapData}
                        />
                    ) : (
                        <USACountryChart
                            chartOptions={USA_CHART_OPTIONS}
                            mapSeries={stateMapSeries}
                        />
                    )}
                </Grid>

                <Grid item lg={5} xl={4}>
                    <Box className="container-p-24-br-16 bg-gray-50 mt-24px">
                        {/* Location Info */}
                        <Box className="flex-column gap-4">
                            <Typography className="text-lg-semi-bold gray-900">
                                {customisation.breakdown} {title}
                            </Typography>
                            {!hideOverAll.includes(company.id) && (
                                <Typography className="text-sm-regular gray-500">
                                    {isLoading ? (
                                        <Skeleton variant="text" width={100} />
                                    ) : (
                                        customisation?.count || 0
                                    )}{' '}
                                    {sub_title}
                                </Typography>
                            )}
                        </Box>
                        <Box className="pt-24px district-breakdown-bar-chart">
                            {isLoading ? (
                                <Skeleton
                                    height={300}
                                    variant="rectangular"
                                    width="100%"
                                />
                            ) : (
                                <HighchartBarChart
                                    chartOptions={chartOptions}
                                    key={`chart-${selectedBreakdown}-${company.id}`}
                                    seriesData={customisation.chartSeries}
                                    seriesName="Digital Engagement"
                                />
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default DistrictBreakdown;
